// API configuration
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const API_KEY = process.env.REACT_APP_API_KEY;

// Helper to get default headers with API key
const getHeaders = (additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  // Add API key if configured
  if (API_KEY) {
    headers["x-api-key"] = API_KEY;
  }

  return headers;
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `HTTP error! status: ${response.status}`
    );
  }
  return response.json();
};

// Helper function to handle API errors
const handleError = (error) => {
  console.error("API Error:", error);
  throw error;
};

export const casinoAPI = {
  // Get all casinos with filtering
  getCasinos: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      // Add filters to params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, value);
        }
      });

      const url = `${API_BASE_URL}/casinos?${params}`;

      const response = await fetch(url, {
        method: "GET",
        headers: getHeaders(),
      });

      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Get available periods (years and months)
  getPeriods: async () => {
    try {
      const url = `${API_BASE_URL}/casinos?periods=true`;
      const response = await fetch(url, {
        method: "GET",
        headers: getHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Get specific casino with history
  getCasino: async (id, includeHistory = true) => {
    try {
      const params = new URLSearchParams({
        includeHistory: includeHistory.toString(),
      });

      const response = await fetch(`${API_BASE_URL}/casinos/${id}?${params}`, {
        headers: getHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Get location summary
  getLocationSummary: async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/casinos/locations/summary`,
        {
          headers: getHeaders(),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Get year-over-year comparison
  getComparison: async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/casinos/analytics/comparison`,
        {
          headers: getHeaders(),
        }
      );
      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Upload and process PDF
  uploadPDF: async (file, onProgress = null) => {
    try {
      const formData = new FormData();
      formData.append("pdf", file);

      // For file uploads, don't set Content-Type (browser sets it with boundary)
      const headers = {};
      if (API_KEY) {
        headers["x-api-key"] = API_KEY;
      }

      const response = await fetch(`${API_BASE_URL}/process-pdf`, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      const result = await handleResponse(response);
      return { success: true, ...result };
    } catch (error) {
      console.error("PDF upload error:", error);
      return { success: false, error: error.message };
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        headers: getHeaders(),
      });
      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Create new casino (admin function - requires adminApiKey)
  createCasino: async (casinoData, adminApiKey = null) => {
    try {
      const headers = getHeaders();
      if (adminApiKey) {
        headers["x-admin-key"] = adminApiKey;
      }

      const response = await fetch(`${API_BASE_URL}/casinos`, {
        method: "POST",
        headers,
        body: JSON.stringify(casinoData),
      });

      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Update casino (admin function - requires adminApiKey)
  updateCasino: async (id, casinoData, adminApiKey = null) => {
    try {
      const headers = getHeaders();
      if (adminApiKey) {
        headers["x-admin-key"] = adminApiKey;
      }

      const response = await fetch(`${API_BASE_URL}/casinos/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(casinoData),
      });

      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Delete casino (admin function - requires adminApiKey)
  deleteCasino: async (id, adminApiKey = null) => {
    try {
      const headers = getHeaders();
      if (adminApiKey) {
        headers["x-admin-key"] = adminApiKey;
      }

      const response = await fetch(`${API_BASE_URL}/casinos/${id}`, {
        method: "DELETE",
        headers,
      });

      return await handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};


export default casinoAPI;
